import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { AxiosError } from 'axios';
import { Input } from '@/components/ui/Input';
import { CompactSortMenu, type CompactSortOption } from '@/components/ui/CompactSortMenu';
import { Button } from '@/components/ui/Button';
import { Icon } from '@/components/ui/Icon';
import { ICONS } from '@/constants/icons';
import { CanvaDesignForUI } from '@/types/canva';
import { SelectedDesign } from './CanvaImportDialog';
import { CanvaDesignCard } from './CanvaDesignCard';

/**
 * CanvaDesignBrowser Component
 *
 * Step 2: Browse designs with search, sort, and multi-select
 */

interface CanvaDesignBrowserProps {
  designs: CanvaDesignForUI[];
  selectedDesigns: SelectedDesign[];
  onToggleSelect: (design: CanvaDesignForUI) => void;
  onSelectAll?: (designs: CanvaDesignForUI[]) => void;
  onDeselectAll?: () => void;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  sortBy: 'modified_descending' | 'title_ascending';
  onSortChange: (sortBy: 'modified_descending' | 'title_ascending') => void;
  hasMore: boolean;
  onLoadMore: () => void;
  isLoading: boolean;
  isFetchingMore: boolean;
  isError?: boolean;
  error?: Error | AxiosError | null;
  onRetry?: () => void;
  onReconnect?: () => void;
}

export const CanvaDesignBrowser = ({
  designs,
  selectedDesigns,
  onToggleSelect,
  onSelectAll,
  onDeselectAll,
  searchQuery,
  onSearchChange,
  sortBy,
  onSortChange,
  hasMore,
  onLoadMore,
  isLoading,
  isFetchingMore,
  isError = false,
  error = null,
  onRetry,
  onReconnect,
}: CanvaDesignBrowserProps) => {
  const { t } = useTranslation('common');
  const [debouncedQuery, setDebouncedQuery] = useState(searchQuery);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      // Trim so whitespace-only input counts as "no search"
      onSearchChange(debouncedQuery.trim());
    }, 300);

    return () => clearTimeout(timer);
  }, [debouncedQuery, onSearchChange]);

  const sortOptions: CompactSortOption<CanvaDesignBrowserProps['sortBy']>[] = [
    { value: 'modified_descending', label: t('modified_newest'), icon: ICONS.CALENDAR, direction: 'down' },
    { value: 'title_ascending', label: t('title_az'), icon: ICONS.TEXT_OUTLINE, direction: 'up' },
  ];

  // Loading, or an empty page that still has a continuation (auto-fetching the next page)
  const isWaitingForDesigns = isLoading || (designs.length === 0 && hasMore);
  const hasDesigns = !isWaitingForDesigns && designs.length > 0;

  const isSearching = searchQuery.trim().length > 0;

  // Check if all visible designs are selected
  const allVisibleSelected = designs.length > 0 && designs.every((design) =>
    selectedDesigns.some((d) => d.design.id === design.id)
  );

  const handleSelectAll = () => {
    if (onSelectAll && designs.length > 0) {
      onSelectAll(designs);
    }
  };

  const handleDeselectAll = () => {
    if (onDeselectAll) {
      onDeselectAll();
    }
  };

  // Detect reauth error via errorCode (status-agnostic, as per backend contract)
  const isReauthRequired =
    isError &&
    error instanceof Error &&
    'response' in error &&
    (error as AxiosError<{ errorCode?: string }>).response?.data?.errorCode === 'CANVA_REAUTH_REQUIRED';

  // Error state — must render BEFORE the empty-state check so a failed
  // request never falls through to "No Designs Found".
  if (isError) {
    if (isReauthRequired) {
      return (
        <div className="text-center py-12">
          <div className="mx-auto w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mb-4">
            <Icon name={ICONS.CAUTION} size="lg" color="text-amber-500" ariaHidden />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            {t('canva_connection_expired')}
          </h3>
          <p className="text-sm text-gray-600 mb-6">
            {t('canva_reconnect_prompt')}
          </p>
          {onReconnect && (
            <Button variant="primary" onClick={onReconnect}>
              {t('canva_reconnect_action')}
            </Button>
          )}
        </div>
      );
    }

    return (
      <div className="text-center py-12">
        <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
          <Icon name={ICONS.CAUTION} size="lg" color="text-gray-400" ariaHidden />
        </div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          {t('canva_designs_load_error')}
        </h3>
        <p className="text-sm text-gray-600 mb-6">
          {t('canva_designs_load_error_prompt')}
        </p>
        {onRetry && (
          <Button variant="secondary" onClick={onRetry}>
            {t('try_again')}
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Filters and batch actions */}
      <div className="space-y-3">
        <div className={isSearching ? 'flex flex-col gap-3 sm:flex-row sm:items-center' : 'flex items-center gap-3'}>
          <div className="min-w-0 flex-1">
            <Input
              type="text"
              placeholder={t('search_designs')}
              value={debouncedQuery}
              onChange={(e) => setDebouncedQuery(e.target.value)}
              className="h-11 !py-0"
            />
          </div>
          <div className={isSearching ? 'w-full shrink-0 sm:w-56' : 'h-11 w-[52px] shrink-0'}>
            {isSearching ? (
              // With a search term Canva ranks by relevance and ignores any sort
              <div
                role="status"
                aria-label={t('canva_sort_best_match_label')}
                className="flex h-11 items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 text-sm text-gray-600"
              >
                <Icon name={ICONS.SEARCH} size="sm" color="text-gray-400" ariaHidden />
                {t('canva_sort_best_match')}
              </div>
            ) : (
              <CompactSortMenu
                label={t('sort_by')}
                options={sortOptions}
                value={sortBy}
                onValueChange={onSortChange}
              />
            )}
          </div>
        </div>

        {/* Batch selection controls and counter */}
        {hasDesigns && (
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <Button
                variant="secondary"
                size="sm"
                onClick={handleSelectAll}
                disabled={allVisibleSelected}
              >
                {t('select_all')}
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={handleDeselectAll}
                disabled={selectedDesigns.length === 0}
              >
                {t('deselect_all')}
              </Button>
            </div>

            {/* Selection counter badge */}
            {selectedDesigns.length > 0 && (
              <div className="flex items-center gap-2 px-3 py-1.5 bg-primary-100 text-primary-700 rounded-full border border-primary-200">
                <Icon name={ICONS.CHECK_CIRCLE} size="sm" color="text-primary-600" ariaHidden />
                <span className="text-sm font-medium">
                  {t('canva_designs_selected', { count: selectedDesigns.length })}
                </span>
              </div>
            )}
          </div>
        )}

        {/* Keyboard hint */}
        {hasDesigns && (
          <p className="text-xs text-gray-500 flex items-center gap-1">
            <Icon name={ICONS.INFO} size="sm" color="text-gray-400" ariaHidden />
            {t('canva_keyboard_hint')}
          </p>
        )}
      </div>

      {/* Designs grid */}
      {isWaitingForDesigns ? (
        <div
          role="status"
          aria-busy="true"
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
        >
          <span className="sr-only">{t('loading')}</span>
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="aspect-square bg-gray-100 rounded-lg animate-pulse" />
          ))}
        </div>
      ) : designs.length === 0 ? (
        <div className="text-center py-12">
          <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
            <Icon name={ICONS.IMAGE} size="lg" color="text-gray-400" ariaHidden />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2 break-words">
            {isSearching
              ? t('canva_no_designs_match', { term: searchQuery.trim() })
              : t('no_designs_found')}
          </h3>
          <p className="text-sm text-gray-600">
            {isSearching
              ? t('canva_no_designs_match_hint')
              : t('no_designs_in_account')}
          </p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {designs.map((design) => {
              const isSelected = selectedDesigns.some(
                (d) => d.design.id === design.id
              );
              return (
                <CanvaDesignCard
                  key={design.id}
                  design={design}
                  isSelected={isSelected}
                  onToggleSelect={() => onToggleSelect(design)}
                />
              );
            })}
          </div>

          {/* Load more */}
          {hasMore && (
            <div className="flex justify-center pt-4">
              <Button
                variant="secondary"
                onClick={onLoadMore}
                loading={isFetchingMore}
                disabled={isFetchingMore}
              >
                {isFetchingMore ? t('loading') : t('load_more')}
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
};
